import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { Loader2, Send, Package, Plus, Trash2, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PublicOfferForm() {
  const { language, t, toggleLanguage } = useLanguage();
  const { categorySlug, productSlug } = useParams();
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productTemplates, setProductTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templatesDialog, setTemplatesDialog] = useState(false);
  const [templateFilterCategory, setTemplateFilterCategory] = useState('');
  const [customProductIndexes, setCustomProductIndexes] = useState(new Set());

  // Dynamic validation schema based on language
  const productItemSchema = z.object({
    category_id: z.string().min(1, t('selectCategoryFirst')),
    product_id: z.string().min(1, t('selectProductFirst')),
    custom_product_name: z.string().optional(),
    specifications: z.string().min(10, t('minChars').replace('{count}', '10')),
    quantity: z.string().min(1, t('required')),
    price: z.string().min(1, t('required')),
  });

  const offerSchema = z.object({
    products: z.array(productItemSchema).min(1, t('minChars').replace('{count}', '1')),
    payment_terms: z.string().min(1, t('required')),
    company_name: z.string().min(3, t('minChars').replace('{count}', '3')),
    fiscal_code: z.string().min(5, t('minChars').replace('{count}', '5')),
    locality: z.string().min(2, t('minChars').replace('{count}', '2')),
    contact_name: z.string().min(3, t('minChars').replace('{count}', '3')),
    contact_email: z.string().email(t('invalidEmail')),
    contact_phone: z.string().min(10, t('invalidPhone')),
  });

  const form = useForm({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      products: [
        {
          category_id: '',
          product_id: '',
          custom_product_name: '',
          specifications: '',
          quantity: '',
          price: '',
        },
      ],
      payment_terms: '',
      company_name: '',
      fiscal_code: '',
      locality: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'products',
  });

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchProductTemplates();
  }, []);

  // Pre-fill from URL if slugs are provided
  useEffect(() => {
    if (categorySlug && productSlug) {
      loadTemplateFromUrl();
    }
  }, [categorySlug, productSlug, productTemplates]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error(t('categoryLoadError'));
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setAllProducts(response.data);
    } catch (error) {
      toast.error(t('productLoadError'));
    }
  };

  const fetchProductTemplates = async () => {
    try {
      const response = await axios.get(`${API}/product-templates`);
      setProductTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadTemplateFromUrl = async () => {
    try {
      const response = await axios.get(`${API}/product-template/${categorySlug}/${productSlug}`);
      const template = response.data;
      
      // Pre-fill the first product with template data - use setValue with shouldValidate: false
      form.setValue('products.0.category_id', template.category_id, { shouldValidate: false });
      // Small delay to ensure category is set before setting product
      setTimeout(() => {
        form.setValue('products.0.product_id', template.product_id, { shouldValidate: false });
        form.setValue('products.0.specifications', template.specifications, { shouldValidate: false });
        form.setValue('products.0.quantity', template.quantity, { shouldValidate: false });
        form.setValue('products.0.price', '', { shouldValidate: false });
        
        // Trigger re-render
        form.trigger('products.0');
      }, 100);
      
      toast.success(language === 'ro' 
        ? `Formular pre-completat cu ${template.product_name}` 
        : `Form pre-filled with ${template.product_name}`);
    } catch (error) {
      console.error('Error loading template from URL:', error);
    }
  };

  const handleSelectTemplate = (template) => {
    // Add template as new product or replace first if empty
    const newProduct = {
      category_id: template.category_id,
      product_id: template.product_id,
      specifications: template.specifications,
      quantity: template.quantity,
      price: '',
    };

    if (fields.length === 1 && !form.getValues('products.0.category_id')) {
      // Replace first empty product - set values individually with delay for Select to update
      form.setValue('products.0.category_id', template.category_id, { shouldValidate: false });
      setTimeout(() => {
        form.setValue('products.0.product_id', template.product_id, { shouldValidate: false });
        form.setValue('products.0.specifications', template.specifications, { shouldValidate: false });
        form.setValue('products.0.quantity', template.quantity, { shouldValidate: false });
        form.setValue('products.0.price', '', { shouldValidate: false });
        form.trigger('products.0');
      }, 100);
    } else {
      // Add as new product
      append(newProduct);
    }

    setTemplatesDialog(false);
    toast.success(language === 'ro'
      ? `Template adăugat: ${template.product_name}`
      : `Template added: ${template.product_name}`);
  };

  const getFilteredProducts = (categoryId) => {
    return allProducts.filter((p) => p.category_id === categoryId);
  };

  const addProduct = () => {
    append({
      category_id: '',
      product_id: '',
      specifications: '',
      quantity: '',
      price: '',
    });
  };

  const removeProduct = (index) => {
    if (fields.length > 1) {
      remove(index);
    } else {
      toast.error(t('minOneProduct'));
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await axios.post(`${API}/offers`, data);
      const successMsg = language === 'ro' 
        ? `Oferta cu ${data.products.length} ${data.products.length === 1 ? 'produs' : 'produse'} a fost trimisă cu succes!`
        : `Offer with ${data.products.length} ${data.products.length === 1 ? 'product' : 'products'} submitted successfully!`;
      toast.success(successMsg);
      form.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('offerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-gray-700" />
            <div>
              <h1 className="text-xl font-semibold text-gray-800">{t('companyName')}</h1>
              <p className="text-sm text-gray-500">{t('systemName')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className="px-2 py-1 text-xs font-semibold text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              data-testid="language-toggle"
            >
              {language === 'ro' ? 'RO' : 'EN'}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (window.location.href = '/admin/login')}
              data-testid="admin-login-link"
            >
              {t('admin')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">{t('submitOffer')}</h2>
            <p className="text-sm text-gray-600">{t('formDescription')}</p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              {/* Products Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-700">{t('offeredProducts')}</h3>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setTemplatesDialog(true)}
                      data-testid="our-specifications-button"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      {language === 'ro' ? 'Specificațiile noastre' : 'Our Specifications'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addProduct}
                      data-testid="add-product-button"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t('addProduct')}
                    </Button>
                  </div>
                </div>

                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-700">
                        {t('product')} #{index + 1}
                      </h4>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeProduct(index)}
                          data-testid={`remove-product-${index}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`products.${index}.category_id`}
                        render={({ field: formField }) => (
                          <FormItem>
                            <FormLabel>{t('category')}</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                formField.onChange(value);
                                form.setValue(`products.${index}.product_id`, '');
                              }}
                              value={formField.value}
                            >
                              <FormControl>
                                <SelectTrigger data-testid={`category-select-${index}`}>
                                  <SelectValue placeholder={t('selectCategory')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {categories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`products.${index}.product_id`}
                        render={({ field: formField }) => {
                          const selectedCategoryId = form.watch(`products.${index}.category_id`);
                          const filteredProducts = getFilteredProducts(selectedCategoryId);
                          const isCustomProduct = formField.value === 'custom';
                          
                          return (
                            <FormItem>
                              <FormLabel>{t('product')}</FormLabel>
                              <Select
                                onValueChange={(value) => {
                                  formField.onChange(value);
                                  if (value === 'custom') {
                                    setCustomProductIndexes(prev => new Set(prev).add(index));
                                  } else {
                                    setCustomProductIndexes(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(index);
                                      return newSet;
                                    });
                                    form.setValue(`products.${index}.custom_product_name`, '');
                                  }
                                }}
                                value={formField.value}
                                disabled={!selectedCategoryId}
                              >
                                <FormControl>
                                  <SelectTrigger data-testid={`product-select-${index}`}>
                                    <SelectValue placeholder={t('selectProduct')} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {filteredProducts.map((prod) => (
                                    <SelectItem key={prod.id} value={prod.id}>
                                      {prod.name}
                                    </SelectItem>
                                  ))}
                                  <SelectItem value="custom" className="font-semibold text-blue-600">
                                    {language === 'ro' ? '➕ Produsul meu nu este în listă' : '➕ My product is not in the list'}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      {/* Custom Product Name Input */}
                      {customProductIndexes.has(index) && (
                        <FormField
                          control={form.control}
                          name={`products.${index}.custom_product_name`}
                          render={({ field: formField }) => (
                            <FormItem>
                              <FormLabel>
                                {language === 'ro' ? 'Denumire Produs Nou' : 'New Product Name'}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={language === 'ro' ? 'Introduceți denumirea produsului' : 'Enter product name'}
                                  {...formField}
                                  data-testid={`custom-product-name-${index}`}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>

                    <FormField
                      control={form.control}
                      name={`products.${index}.specifications`}
                      render={({ field: formField }) => (
                        <FormItem>
                          <FormLabel>{t('productSpecifications')}</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t('describeSpecifications')}
                              className="min-h-20"
                              data-testid={`specifications-input-${index}`}
                              {...formField}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`products.${index}.quantity`}
                        render={({ field: formField }) => (
                          <FormItem>
                            <FormLabel>{t('minimumQuantity')}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t('quantityExample')}
                                data-testid={`quantity-input-${index}`}
                                {...formField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`products.${index}.price`}
                        render={({ field: formField }) => (
                          <FormItem>
                            <FormLabel>{t('price')}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t('priceExample')}
                                data-testid={`price-input-${index}`}
                                {...formField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment Terms */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700 pb-2 border-b border-gray-200">
                  {t('paymentConditions')}
                </h3>
                <FormField
                  control={form.control}
                  name="payment_terms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('paymentTerms')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('paymentTermsExample')}
                          data-testid="payment-terms-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Company Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700 pb-2 border-b border-gray-200">
                  {t('companyData')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="company_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('companyName2')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('companyNameExample')}
                            data-testid="company-name-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="fiscal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('fiscalCode')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('fiscalCodeExample')}
                            data-testid="fiscal-code-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="locality"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('locality')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('localityExample')}
                            data-testid="locality-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700 pb-2 border-b border-gray-200">
                  {t('contactPerson')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="contact_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('contactName')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('contactNameExample')}
                            data-testid="contact-name-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('contactEmail')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t('contactEmailExample')}
                            data-testid="contact-email-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('contactPhone')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('contactPhoneExample')}
                            data-testid="contact-phone-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full md:w-auto"
                  disabled={loading}
                  data-testid="submit-offer-button"
                  style={{ backgroundColor: '#1a73e8' }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {t('submitButton')} ({fields.length}{' '}
                      {fields.length === 1 ? t('products') : t('productsPlural')})
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>

      {/* Product Templates Dialog */}
      <Dialog open={templatesDialog} onOpenChange={setTemplatesDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'ro' ? 'Specificațiile Noastre' : 'Our Specifications'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-4">
              {language === 'ro'
                ? 'Selectați un produs pentru a pre-completa specificațiile și cantitatea. Veți completa doar prețul.'
                : 'Select a product to pre-fill specifications and quantity. You will only need to fill in the price.'}
            </p>
            
            {/* Category Filter */}
            <div className="mb-4">
              <Select
                value={templateFilterCategory || 'all'}
                onValueChange={(value) => setTemplateFilterCategory(value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-full md:w-64" data-testid="template-category-filter">
                  <SelectValue placeholder={language === 'ro' ? 'Toate categoriile' : 'All categories'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {language === 'ro' ? 'Toate categoriile' : 'All categories'}
                  </SelectItem>
                  {[...new Set(productTemplates.map(t => t.category_id))].map(catId => {
                    const category = categories.find(c => c.id === catId);
                    return category ? (
                      <SelectItem key={catId} value={catId}>
                        {category.name}
                      </SelectItem>
                    ) : null;
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {productTemplates
                .filter(template => !templateFilterCategory || template.category_id === templateFilterCategory)
                .map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectTemplate(template)}
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{template.product_name}</h4>
                      <p className="text-xs text-gray-500 mt-1">{template.category_name}</p>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {template.specifications}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        <strong>MOQ:</strong> {template.quantity}
                      </p>
                    </div>
                    <Plus className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ))}
            </div>
            {productTemplates.filter(t => !templateFilterCategory || t.category_id === templateFilterCategory).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {language === 'ro'
                  ? (productTemplates.length === 0 
                      ? 'Nu există template-uri disponibile momentan.' 
                      : 'Nu există produse în categoria selectată.')
                  : (productTemplates.length === 0
                      ? 'No templates available at the moment.'
                      : 'No products in selected category.')}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PublicOfferForm;
